import { useEffect, useState } from "react";
import api from "@/lib/api";

const EMPTY = {
  label: "Home", recipient: "", line1: "", line2: "",
  city: "", state: "", postal_code: "", country: "US",
  phone: "", is_default: false, delivery_notes: "",
};

export default function AddressBook({ embedded = false }) {
  const [addrs, setAddrs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");

  const load = () => api.get("/addresses").then(r => setAddrs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (editing) await api.put(`/addresses/${editing}`, form);
      else await api.post("/addresses", form);
      setEditing(null); setForm(EMPTY); load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    await api.delete(`/addresses/${id}`);
    load();
  };

  const edit = (a) => { setEditing(a.id); setForm({...EMPTY, ...a}); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  return (
    <div data-testid="address-book" className={embedded ? "" : "max-w-4xl mx-auto px-6 py-12"}>
      {!embedded && (
        <>
          <p className="overline">Delivery</p>
          <h1 className="font-display text-5xl mt-3">Your addresses.</h1>
        </>
      )}

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {addrs.map(a => (
          <div key={a.id} className="card-flat p-5" data-testid={`addr-${a.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="overline">{a.label}{a.is_default && " · default"}</p>
                <p className="font-medium mt-2">{a.recipient}</p>
                <p className="text-sm text-[var(--ff-text-secondary)]">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</p>
                <p className="text-sm text-[var(--ff-text-secondary)]">{a.city}, {a.state} {a.postal_code}</p>
                {a.phone && <p className="text-xs text-[var(--ff-text-muted)] mt-1">{a.phone}</p>}
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => edit(a)} className="text-xs underline" data-testid={`addr-edit-${a.id}`}>Edit</button>
                <button onClick={() => remove(a.id)} className="text-xs underline text-red-700" data-testid={`addr-del-${a.id}`}>Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={submit} className="card-flat p-6 mt-6 grid md:grid-cols-2 gap-4" data-testid="addr-form">
        <h3 className="font-display text-2xl md:col-span-2">{editing ? "Edit address" : "Add a new address"}</h3>
        <input className="input-base" placeholder="Label (Home, Work)" value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="addr-label"/>
        <input className="input-base" placeholder="Recipient name" value={form.recipient} onChange={e => setForm({...form, recipient: e.target.value})} required data-testid="addr-recipient"/>
        <input className="input-base md:col-span-2" placeholder="Street address" value={form.line1} onChange={e => setForm({...form, line1: e.target.value})} required data-testid="addr-line1"/>
        <input className="input-base md:col-span-2" placeholder="Apt / suite (optional)" value={form.line2 || ""} onChange={e => setForm({...form, line2: e.target.value})} data-testid="addr-line2"/>
        <input className="input-base" placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} required data-testid="addr-city"/>
        <input className="input-base" placeholder="State (e.g. FL)" maxLength={2} value={form.state} onChange={e => setForm({...form, state: e.target.value.toUpperCase()})} required data-testid="addr-state"/>
        <input className="input-base" placeholder="ZIP code" value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} required data-testid="addr-zip"/>
        <input className="input-base" placeholder="Phone (for delivery driver)" value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} data-testid="addr-phone"/>
        <textarea className="input-base md:col-span-2" rows={2} placeholder="Delivery notes (gate code, leave at door, etc.)" value={form.delivery_notes || ""} onChange={e => setForm({...form, delivery_notes: e.target.value})} data-testid="addr-notes"/>
        <label className="flex items-center gap-2 md:col-span-2 text-sm">
          <input type="checkbox" checked={form.is_default} onChange={e => setForm({...form, is_default: e.target.checked})} data-testid="addr-default"/>
          Set as default delivery address
        </label>
        {err && <p className="text-sm text-red-700 md:col-span-2">{err}</p>}
        <div className="md:col-span-2 flex gap-3">
          <button type="submit" className="btn-primary" data-testid="addr-submit">{editing ? "Save changes" : "Add address"}</button>
          {editing && <button type="button" onClick={cancel} className="btn-secondary">Cancel</button>}
        </div>
      </form>
    </div>
  );
}
